import React from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'

/*
 * columns: array of strings or function (components) example: ['name', 'email', 'phone']
 * data: array of objects with children array of objects with item and style
 */
/**
 * CustomTable component renders a simple HTML table with optional headers and a loading state.
 * It accepts an array of column definitions and row data, and displays a "Loading..." row when loading.
 *
 * @component
 * @param {Object} props
 * @param {Array<string|Object>} [props.columns] – Array of column definitions. Each element can be:
 *   - A string or React node to render as the header cell.
 *   - An object with `{ item: string|Function, style: Object }` to customize cell rendering and styling.
 * @param {Array<Array|string|Object>} props.data – Array of row data. Each row can be:
 *   - An array of cell values (string or Function).
 *   - An object with a `children` array of `{ item: string|Function, style: Object }` entries.
 * @param {boolean} [props.loading=false] – When true, displays a single row with "Loading..." spanning all columns.
 * @returns {JSX.Element}
 */
const CustomTable = observer(({ columns, data, loading = false, ...props }) => {
	return (
		<table className="table-simple we-custom-table" {...props}>
			{columns && (
				<thead>
					<tr>
						{columns.map((column, index) => {
							const columnItem =
								typeof column === 'object'
									? column.item
									: column
							return (
								<th key={index} style={column?.style || {}}>
									{typeof columnItem === 'function'
										? columnItem()
										: columnItem}
								</th>
							)
						})}
					</tr>
				</thead>
			)}
			<tbody>
				{loading ? (
					<tr>
						<td colSpan={columns.length || data[0].length}>
							Loading...
						</td>
					</tr>
				) : (
					data.map((row, index) => {
						const cells = Array.isArray(row)
							? row
							: row.children || []
						return (
							<tr key={index}>
								{cells.map((cell, index) => {
									if (!cell) return null
									if (typeof cell === 'string') {
										return <td key={index}>{cell}</td>
									} else if (typeof cell === 'function') {
										return <td key={index}>{cell()}</td>
									}
									return (
										<td
											key={index}
											style={cell?.style || {}}
										>
											{typeof cell.item === 'function'
												? cell.item()
												: cell.item}
										</td>
									)
								})}
							</tr>
						)
					})
				)}
			</tbody>
		</table>
	)
})

CustomTable.propTypes = {
	columns: PropTypes.array,
	data: PropTypes.array.isRequired,
	loading: PropTypes.bool
}

export default CustomTable
